import React from 'react';
import { IWebhookEndpoint } from '../../../../networked-shared-interfaces/community-admin/external-vendor-webhooks';
import { disabledReasonLabel, formatTime, summarizeEvents } from './webhookHelpers';

interface IProps {
	endpoints: IWebhookEndpoint[];
	maxEndpoints: number;
	busyId?: string | null;
	onAdd: () => void;
	onEdit: (e: IWebhookEndpoint) => void;
	onOpen: (e: IWebhookEndpoint) => void;
	onToggle: (e: IWebhookEndpoint) => void;
	onDelete: (e: IWebhookEndpoint) => void;
}

const WebhookEndpointsList = ({ endpoints, maxEndpoints, busyId, onAdd, onEdit, onOpen, onToggle, onDelete }: IProps) => {
	const atCap = endpoints.length >= maxEndpoints;
	return (
		<div>
			<div className="d-flex justify-content-between align-items-center mb-2">
				<h4 className="mb-0">Webhooks</h4>
				<button type="button" className="btn btn-success" onClick={onAdd} disabled={atCap}>Add endpoint</button>
			</div>
			{atCap && <small className="form-text text-muted mb-2">Limit of {maxEndpoints} endpoints reached. Delete one to add another.</small>}
			{endpoints.length === 0 ? (
				<div className="alert alert-info"><i className="fas fa-info-circle mr-2"></i>No webhook endpoints yet.</div>
			) : (
				<div className="table-responsive">
					<table className="table table-sm table-bordered">
						<thead>
							<tr><th>Name</th><th>URL</th><th>Status</th><th>Events</th><th>Last delivery</th><th /></tr>
						</thead>
						<tbody>
							{endpoints.map((e) => {
								const summary = summarizeEvents(e);
								const active = e.status === 'active';
								const spaces = e.filters?.spaceIds?.length ?? 0;
								return (
									<tr key={e.id}>
										<td>{e.name || '—'}</td>
										<td style={{ wordBreak: 'break-all' }}>{e.url}</td>
										<td>
											<span className={`badge ${active ? 'badge-success' : 'badge-secondary'}`}>{active ? 'Active' : 'Disabled'}</span>
											{!active && e.disabledReason && <small className="text-muted ml-1">{disabledReasonLabel(e.disabledReason)}</small>}
										</td>
										<td>
											{summary ? `${summary.groups} groups, ${summary.events} events` : 'No events'}
											{spaces > 0 && <small className="text-muted"> · {spaces} spaces</small>}
										</td>
										<td title={formatTime(e.lastDelivery?.createdAt)}>
											{e.lastDelivery ? `${e.lastDelivery.status}${e.lastDelivery.httpStatus ? ` (${e.lastDelivery.httpStatus})` : ''}` : '—'}
										</td>
										<td className="text-nowrap">
											<button type="button" className="btn btn-link btn-sm" onClick={() => onOpen(e)}>Secret, test &amp; log</button>
											<button type="button" className="btn btn-link btn-sm" onClick={() => onEdit(e)}>Edit</button>
											<button type="button" className="btn btn-link btn-sm" disabled={busyId === e.id} onClick={() => onToggle(e)}>{active ? 'Disable' : 'Enable'}</button>
											<button type="button" className="btn btn-link btn-sm text-danger" disabled={busyId === e.id} onClick={() => onDelete(e)}>Delete</button>
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			)}
		</div>
	);
};

export default WebhookEndpointsList;
